/**
 * PictureLetterCardSVG — Phase 1, Component 12 of the TeachBud Representation Catalog
 *
 * Renders a picture-letter card per §5.2 Phonemic Awareness:
 *   - Large uppercase letter (top left) + lowercase letter (top right)
 *   - Central illustration placeholder (image box with object name)
 *   - Dotted tracing guide below: practice writing the letter 3 times
 *   - Grades K–1 ELA
 *
 * Note: the central image is a placeholder with the object name — actual
 * illustrations will be swapped in once an image asset pipeline is in place.
 */

import React from 'react';

export interface PictureLetterCardProps {
  /** The letter to show and practice. Default 'A'. */
  letter?: string;
  /** Object name whose first sound matches the letter. Default auto. */
  objectName?: string;
  /** When true, show tracing dots only — no pre-drawn letter strokes. */
  studentMode?: boolean;
  /** Override pixel width. */
  width?: number;
  className?: string;
}

// ── Layout ────────────────────────────────────────────────────────────────────
const VB_W = 360;
const VB_H = 440;
const PAD  = 16;

// Letter area at top
const LETTER_AREA_H = 80;
// Image area in middle
const IMG_AREA_Y = PAD + LETTER_AREA_H + 8;
const IMG_AREA_H = 160;
const IMG_AREA_W = VB_W - 2 * PAD;
// Tracing area at bottom
const TRACE_Y = IMG_AREA_Y + IMG_AREA_H + 12;

// Default object name map (a→z)
const LETTER_OBJECTS: Record<string, string> = {
  A: 'apple', B: 'ball', C: 'cat', D: 'dog', E: 'egg',
  F: 'fish', G: 'goat', H: 'hat', I: 'igloo', J: 'jar',
  K: 'kite', L: 'lion', M: 'moon', N: 'nest', O: 'owl',
  P: 'pencil', Q: 'queen', R: 'rabbit', S: 'sun', T: 'tree',
  U: 'umbrella', V: 'violin', W: 'worm', X: 'box', Y: 'yarn', Z: 'zebra',
};

// Colors
const CARD_BG     = '#FFFBEB';  // amber-50 (K warmth)
const CARD_STROKE = '#F59E0B';  // amber
const LETTER_COLOR= '#2563EB';  // TeachBud blue
const IMG_BG      = '#EFF6FF';  // blue-50
const IMG_STROKE  = '#BFDBFE';  // blue-200
const TRACE_FILL  = '#BFDBFE';  // blue-200 for tracing dots
const LABEL_COLOR = '#374151';

export function PictureLetterCardSVG({
  letter = 'A',
  objectName,
  studentMode = true,
  width,
  className = '',
}: PictureLetterCardProps) {
  const L = letter.toUpperCase().slice(0, 1);
  const l = L.toLowerCase();
  const objName = objectName ?? LETTER_OBJECTS[L] ?? 'object';

  const svgW = width ?? VB_W;
  const svgH = width ? Math.round((VB_H / VB_W) * width) : VB_H;

  // Tracing dots — 3 letter guides spaced across width
  const traceSlotW = (IMG_AREA_W - 16) / 3;
  const traceY = TRACE_Y + 20;

  // Dotted tracing letter placeholder (3 ghost letters)
  function TracingLetter({ x, y }: { x: number; y: number }) {
    return (
      <text
        x={x} y={y}
        textAnchor="middle"
        fontSize={60}
        fontFamily="Nunito, sans-serif"
        fontWeight={800}
        fill="none"
        stroke={TRACE_FILL}
        strokeWidth={4}
        strokeDasharray="6 4"
      >
        {L}
      </text>
    );
  }

  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      width={svgW} height={svgH}
      className={className}
      role="img"
      aria-label={`Letter card for ${L} — ${objName}`}
    >
      <title>Letter card: {L} for {objName}</title>

      {/* Card background */}
      <rect x={0} y={0} width={VB_W} height={VB_H} rx={16} fill={CARD_BG} stroke={CARD_STROKE} strokeWidth={3} />

      {/* ── Letter header ── */}
      <rect x={0} y={0} width={VB_W} height={LETTER_AREA_H + PAD} rx={16} fill="#FEF3C7" />
      {/* Uppercase letter left */}
      <text
        x={PAD + 32} y={PAD + 58}
        textAnchor="middle"
        fontSize={62}
        fontFamily="Nunito, sans-serif"
        fontWeight={800}
        fill={LETTER_COLOR}
      >
        {L}
      </text>
      {/* Lowercase letter right */}
      <text
        x={VB_W - PAD - 32} y={PAD + 58}
        textAnchor="middle"
        fontSize={62}
        fontFamily="Nunito, sans-serif"
        fontWeight={800}
        fill={LETTER_COLOR}
        opacity={0.7}
      >
        {l}
      </text>
      {/* Phonics prompt center */}
      <text
        x={VB_W / 2} y={PAD + 44}
        textAnchor="middle"
        fontSize={14}
        fontFamily="Inter, sans-serif"
        fontWeight={600}
        fill="#92400E"
      >
        "{l}" says /{l}/
      </text>

      {/* ── Image placeholder ── */}
      <rect
        x={PAD} y={IMG_AREA_Y}
        width={IMG_AREA_W} height={IMG_AREA_H}
        rx={10}
        fill={IMG_BG}
        stroke={IMG_STROKE}
        strokeWidth={2}
        strokeDasharray="6 3"
      />
      {/* Picture placeholder icon (camera emoji) */}
      <text
        x={VB_W / 2} y={IMG_AREA_Y + IMG_AREA_H / 2 - 8}
        textAnchor="middle"
        fontSize={36}
      >
        🖼️
      </text>
      <text
        x={VB_W / 2} y={IMG_AREA_Y + IMG_AREA_H / 2 + 22}
        textAnchor="middle"
        fontSize={18}
        fontFamily="Nunito, sans-serif"
        fontWeight={700}
        fill={LETTER_COLOR}
      >
        {L} is for {objName}
      </text>

      {/* ── Tracing guide ── */}
      <text
        x={PAD} y={TRACE_Y - 2}
        fontSize={11}
        fontFamily="Inter, sans-serif"
        fontWeight={600}
        fill="#9CA3AF"
        letterSpacing={1}
      >
        TRACE THE LETTER
      </text>
      {/* Three tracing boxes */}
      {Array.from({ length: 3 }).map((_, i) => {
        const tx = PAD + 8 + i * traceSlotW;
        return (
          <g key={i}>
            <rect
              x={tx} y={TRACE_Y}
              width={traceSlotW - 8} height={70}
              rx={8}
              fill="none"
              stroke={IMG_STROKE}
              strokeWidth={1.5}
            />
            <TracingLetter x={tx + (traceSlotW - 8) / 2} y={TRACE_Y + 62} />
          </g>
        );
      })}
    </svg>
  );
}

export function parsePictureLetterCardHint(hint: string): PictureLetterCardProps {
  const letterMatch = hint.match(/\bletter\s+([A-Za-z])\b/i) || hint.match(/\b([A-Za-z])\s+is\s+for/i);
  return {
    letter: letterMatch ? letterMatch[1].toUpperCase() : 'A',
    studentMode: true,
  };
}
